// Pure helpers of the linked vehicles console and its transmission log (Account > Apps).
// Transmissions live in px-cloud's CompanyIntegrationLog (written by data-catalog): one item per
// transmission and a daily summary per vehicle, keyed by the account's days in its time zone.
import moment from 'moment-timezone';
import { translateEvent } from 'Modules/.common/utils/translateEvents';

// Daily summaries refresh while the console is open; new rows arrive while the log shows a live range
export const CONSOLE_POLL_MS = 15000;
export const LOG_POLL_MS = 10000;
// The Apps grid follows the transmission log (silent reload) and ages its indicator (clock)
export const GRID_POLL_MS = 60000;
export const GRID_CLOCK_MS = 30000;
// While a sync started here runs, the grid asks for its result (API lastSync) this often, in case the push of the
// result never reaches the browser; after SYNC_MAX_WAIT_MS it stops waiting
export const SYNC_POLL_MS = 5000;
export const SYNC_MAX_WAIT_MS = 30 * 60 * 1000;
// A vehicle whose last transmission is this recent pulses in the console (at least one poll)
export const FRESH_VEHICLE_MS = CONSOLE_POLL_MS + 5000;
// A log row that just arrived keeps its highlight this long
export const FRESH_ROW_MS = 3000;
// Rows the panel keeps (design: "Showing latest 200 of N · download the CSV for the full range")
export const LOG_PAGE_SIZE = 200;
// DynamoDB applies the page size before the filters, so a page can bring fewer rows: pages asked per load
export const LOG_MAX_PAGES = 10;
export const LOG_POLL_MAX_PAGES = 3;
// CSV export: API maximum per page, with a safety cap of 100k rows
export const EXPORT_PAGE_SIZE = 500;
export const EXPORT_MAX_PAGES = 200;
// Provider vehicles: API maximum per page, up to 5000 vehicles
export const UNITS_PAGE_SIZE = 200;
export const UNITS_MAX_PAGES = 25;
// Daily summaries loaded with the console: today and the 6 days before, the log's widest range
export const STATS_DAYS = 7;
export const LOG_RANGES = ['today', 'yesterday', 'last7days'];

/** A zone moment knows, UTC otherwise (the API keys the summaries the same way) */
export const safeTimeZone = (timeZone) => (timeZone && moment.tz.zone(timeZone) ? timeZone : 'UTC');

// Generic name of the US zones that switch to daylight time (design: "times in ET")
const GENERIC_ZONES = { EST: 'ET', EDT: 'ET', CST: 'CT', CDT: 'CT', MST: 'MT', MDT: 'MT', PST: 'PT', PDT: 'PT', AKST: 'AKT', AKDT: 'AKT' };

/**
 * Short name of the account time zone with its city, e.g. "ET (New York)" for America/New_York; the abbreviation
 * alone for UTC, and the IANA name for zones moment only knows by offset ("-03")
 */
export const zoneLabel = (timeZone, now = Date.now()) => {
    const zone = safeTimeZone(timeZone);
    if (zone === 'UTC') return 'UTC';
    const abbr = moment.tz(now, zone).zoneAbbr();
    if (!/^[A-Z]{2,5}$/.test(abbr)) return zone;
    const city = zone.split('/').pop().replace(/_/g, ' ');
    return `${GENERIC_ZONES[abbr] || abbr} (${city})`;
};

// Reason phrases of the HTTP statuses an app can answer (RFC 9110), e.g. "504 Gateway Timeout"
const HTTP_REASONS = {
    400: 'Bad Request', 401: 'Unauthorized', 402: 'Payment Required', 403: 'Forbidden', 404: 'Not Found',
    405: 'Method Not Allowed', 408: 'Request Timeout', 409: 'Conflict', 410: 'Gone', 413: 'Content Too Large',
    415: 'Unsupported Media Type', 422: 'Unprocessable Content', 429: 'Too Many Requests',
    500: 'Internal Server Error', 501: 'Not Implemented', 502: 'Bad Gateway', 503: 'Service Unavailable', 504: 'Gateway Timeout'
};

/** "504 Gateway Timeout", "HTTP 599" for a status without a known phrase, null without a status */
export const httpStatusText = (code) => {
    if (!code) return null;
    return HTTP_REASONS[code] ? `${code} ${HTTP_REASONS[code]}` : `HTTP ${code}`;
};

/** Link of a provider vehicle: matched to a unit and active, active without a unit, or inactive in the app */
export const linkState = (unit) => (unit.status !== 'ACTIVE' ? 'inactive' : unit.unitId ? 'linked' : 'unmatched');

/** providerData as the API returns it (AWSJSON: an object, or a JSON string) */
export const parseProviderData = (value) => {
    if (value && typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
        return null;
    }
};

/** Account days of a log range, oldest first, out of the stats days (oldest first, today last) */
export const rangeDays = (range, days = []) => {
    if (range === 'today') return days.slice(-1);
    if (range === 'yesterday') return days.slice(-2, -1);
    return days.slice(-STATS_DAYS);
};

/** ISO UTC bounds of a log range in the account time zone; live = the range reaches now */
export const rangeBounds = (range, timeZone, now = Date.now()) => {
    const today = moment.tz(now, safeTimeZone(timeZone)).startOf('day');
    if (range === 'yesterday') {
        return {
            from: today.clone().subtract(1, 'day').toISOString(),
            to: today.clone().subtract(1, 'millisecond').toISOString(),
            live: false
        };
    }
    const from = range === 'last7days' ? today.clone().subtract(STATS_DAYS - 1, 'days') : today;
    return { from: from.toISOString(), to: new Date(now).toISOString(), live: true };
};

/** Transmissions (all / errors) of some account days from the daily summaries, optionally of one vehicle */
export const countTransmissions = (items = [], days = [], unitId = null) => items.reduce((acc, item) => {
    if (!days.includes(item.day) || (unitId && item.unitId !== unitId)) return acc;
    return { all: acc.all + (item.sent || 0) + (item.errors || 0), errors: acc.errors + (item.errors || 0) };
}, { all: 0, errors: 0 });

/** Today's summary of each vehicle, by unitId */
export const todayByUnit = (stats) => {
    const today = stats?.days?.[stats.days.length - 1];
    const byUnit = {};
    (stats?.items || []).forEach((item) => {
        if (item.day === today && item.unitId) byUnit[item.unitId] = item;
    });
    return byUnit;
};

/** The summary with the latest transmission */
export const latestTransmission = (items = []) =>
    items.reduce((latest, item) => (item.lastOn && (!latest || item.lastOn > latest.lastOn) ? item : latest), null);

export const formatTime = (iso, timeZone) => moment.tz(iso, safeTimeZone(timeZone)).format('h:mm:ss A');
export const formatDay = (iso, timeZone) => moment.tz(iso, safeTimeZone(timeZone)).format('MMM D');
export const formatStamp = (iso, timeZone) => moment.tz(iso, safeTimeZone(timeZone)).format('YYYY-MM-DD HH:mm:ss');

/** "12s ago", "3 min ago", or the time of day for older dates */
export const relativeTime = (iso, now, timeZone, messages) => {
    const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
    if (seconds < 60) return messages['accountAppsConsole_secondsAgo'].replace('_N_', seconds);
    if (seconds < 3600) return messages['accountAppsConsole_minutesAgo'].replace('_N_', Math.floor(seconds / 60));
    return formatTime(iso, timeZone);
};

/** Like relativeTime, but hours ago within a day and the account day and time beyond it ("Oct 8, 9:22 PM") */
export const sinceText = (iso, now, timeZone, messages) => {
    const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
    if (seconds < 3600) return relativeTime(iso, now, timeZone, messages);
    if (seconds < 86400) return messages['accountAppsConsole_hoursAgo'].replace('_N_', Math.floor(seconds / 3600));
    return moment.tz(iso, safeTimeZone(timeZone)).format('MMM D, h:mm A');
};

// A transmission within this window means the app is transmitting right now (data-catalog sends each reading)
export const LIVE_WINDOW_MS = 15 * 60 * 1000;

/**
 * State of an app's telemetry (API `telemetry`, from the transmission log): 'live' with a transmission in the last
 * minutes, 'idle' with older ones, 'none' without any; null for apps without telemetry.
 */
export const telemetryState = (telemetry, now = Date.now()) => {
    if (!telemetry) return null;
    if (!telemetry.lastOn) return 'none';
    return now - Date.parse(telemetry.lastOn) <= LIVE_WINDOW_MS ? 'live' : 'idle';
};

/** Identity of a transmission in the log (the DynamoDB sort key) */
export const transmissionKey = (row) => `${row.createdOn}#${row.unitId}`;

/** Newest first, each key once, at most `max` rows; `trimmed` when older rows were dropped */
export const mergeTransmissions = (newRows, rows, max = LOG_PAGE_SIZE) => {
    const seen = new Set();
    const merged = [];
    [...newRows, ...rows].forEach((row) => {
        const key = transmissionKey(row);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(row);
    });
    merged.sort((a, b) => (a.createdOn < b.createdOn ? 1 : a.createdOn > b.createdOn ? -1 : 0));
    return { rows: merged.slice(0, max), trimmed: merged.length > max };
};

// CSV columns and the UI text of each header, like the platform's other exports (never the raw key)
const CSV_HEADER_KEYS = [
    'accountAppsLog_csvTime', 'accountAppsLog_csvTimeUtc', 'accountAppsLog_csvUnit', 'accountAppsLog_csvVin',
    'accountAppsLog_csvProviderVehicle', 'accountAppsLog_csvEvent', 'accountAppsLog_csvReadingTime', 'accountAppsLog_csvLatitude',
    'accountAppsLog_csvLongitude', 'accountAppsLog_csvSpeed', 'accountAppsLog_csvOdometer', 'accountAppsLog_csvStatus',
    'accountAppsLog_csvHttpStatus', 'accountAppsLog_csvError', 'accountAppsLog_csvAttempts', 'accountAppsLog_csvLatency'
];

/**
 * Texts of the CSV in the user's language: the headers, the status of each transmission and the reading events
 * (the platform's `event_*` dictionary, the one translateEvent reads). A plain object, so it can travel to the
 * export thunk.
 */
export const transmissionsCsvLabels = (messages, { appName = '', timeZone } = {}) => {
    const text = (key) => (messages[key] || '').replace(/_APP_/g, appName).replace(/_TZ_/g, zoneLabel(timeZone));
    return {
        headers: CSV_HEADER_KEYS.map(text),
        status: { SUCCESS: messages['accountAppsLog_ok'], ERROR: messages['accountAppsLog_error'] },
        events: Object.fromEntries(Object.entries(messages).filter(([key]) => key.startsWith('event_')))
    };
};

// RFC 4180: quote a cell with a comma, a quote or a line break, doubling its quotes
const csvCell = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * CSV of transmissions: times in the account zone and in UTC, unit and VIN from the synced vehicles; headers,
 * status and events in the user's language (`labels` from transmissionsCsvLabels)
 */
export const buildTransmissionsCsv = (rows, { timeZone, unitsById = {}, labels }) => {
    const lines = rows.map((row) => {
        const unit = unitsById[row.unitId] || {};
        return [
            formatStamp(row.createdOn, timeZone), row.createdOn, unit.unitLabel || row.unitId, unit.vin, row.providerUnitId,
            translateEvent(row.event, labels.events), row.unitTime, row.latitude, row.longitude, row.speedMph, row.odometer,
            labels.status[row.status] || row.status, row.httpStatus, row.errorMessage, row.attempts, row.durationMs
        ].map(csvCell).join(',');
    });
    return [labels.headers.map(csvCell).join(','), ...lines].join('\r\n');
};

/** e.g. coastpay-transmissions_today_TRK-104_errors.csv */
export const transmissionsFileName = (integrationCode, range, unitLabel, errorsOnly) => {
    const unit = unitLabel ? `_${String(unitLabel).trim().replace(/[^\w-]+/g, '-')}` : '';
    return `${integrationCode}-transmissions_${range}${unit}${errorsOnly ? '_errors' : ''}.csv`;
};
